/**
 * Voyage Data Composable
 * Handles data fetching for voyage details, summary, and telemetry data
 */

import VoyageSummarySvc from '@/services/VoyageSummarySvc';
import SummarySvc from '@/services/SummarySvc';
import moment from 'moment';

export function useVoyageData() {
  const TELEMETRY_CHUNK_SECONDS = 3 * 24 * 60 * 60;

  const getTelemetryAggregationParams = (voyage) => {
    if (!voyage?.mvygStartTime || !voyage?.mvygEndTime) {
      return {
        aggregatedUnit: 'HOUR',
        aggregatedLength: 1,
      };
    }

    const start = moment(voyage.mvygStartTime);
    const end = moment(voyage.mvygEndTime);

    if (!start.isValid() || !end.isValid()) {
      return {
        aggregatedUnit: 'HOUR',
        aggregatedLength: 1,
      };
    }

    const aggregatedUnit = 'HOUR';
    const secondsLength = Math.max(end.unix() - start.unix(), 0);
    const unitInSeconds = 3600;
    const expectedDataLength = secondsLength / unitInSeconds;
    const maxDataLimit = 50;

    return {
      aggregatedUnit,
      aggregatedLength: expectedDataLength > maxDataLimit
        ? Math.max(parseInt(expectedDataLength / maxDataLimit, 10), 1)
        : 1,
    };
  };

  const buildTelemetryChunks = (start, end) => {
    if (!start || !end || end <= start) {
      return [{ start, end }];
    }

    const chunks = [];
    let currentStart = start;

    while (currentStart < end) {
      const currentEnd = Math.min(currentStart + TELEMETRY_CHUNK_SECONDS, end);
      chunks.push({
        start: currentStart,
        end: currentEnd,
      });
      currentStart = currentEnd;
    }

    return chunks;
  };

  const mergeTelemetryResponses = (responses) => {
    const validResponses = responses.filter(Boolean);

    if (!validResponses.length) {
      return null;
    }

    return validResponses.reduce((merged, current) => {
      const currentData = current?.data || {};
      const mergedData = merged?.data || {};

      return {
        ...merged,
        ...current,
        data: {
          ...mergedData,
          ...currentData,
        },
      };
    }, null);
  };

  const fetchTelemetryByType = async (voyage, devcType) => {
    if (!voyage || !voyage.mvygMassId) {
      return null;
    }

    const start = moment(voyage.mvygStartTime).unix();
    const end = moment(voyage.mvygEndTime).unix();
    const { aggregatedUnit, aggregatedLength } = getTelemetryAggregationParams(voyage);
    const chunks = buildTelemetryChunks(start, end);

    const responses = await Promise.all(chunks.map(async (chunk) => {
      const response = await SummarySvc.getSummary({
        devcType: [devcType],
        devcMassId: voyage.mvygMassId,
        start: chunk.start,
        end: chunk.end,
        aggregatedUnit,
        aggregatedLength,
        showDetails: true,
      });

      return response.data?.data?.[devcType] || response.data?.[devcType] || response.data || null;
    }));

    return mergeTelemetryResponses(responses);
  };

  /**
   * Fetch voyage summary report by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Voyage summary report data or null
   */
  const fetchVoyageSummaryData = async (voyageId, options = {}) => {
    try {
      const response = await VoyageSummarySvc.fetchById(voyageId, options);
      return response?.summary || null;
    } catch (error) {
      console.error('fetchVoyageSummaryData error:', error);
      throw error;
    }
  };

  /**
   * Fetch computed voyage summary metrics by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Summary data or null
   */
  const fetchSummary = async (voyageId, options = {}) => {
    try {
      return await fetchVoyageSummaryData(voyageId, options);
    } catch (error) {
      console.error('fetchSummary error:', error);
      return null;
    }
  };

  /**
   * Fetch GPS data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} GPS data or null
   */
  const fetchGpsData = async (voyage) => {
    try {
      return await fetchTelemetryByType(voyage, 'gps');
    } catch (error) {
      console.error('fetchGpsData error:', error);
      return null;
    }
  };

  /**
   * Fetch RPM data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} RPM data or null
   */
  const fetchRpmData = async (voyage) => {
    try {
      return await fetchTelemetryByType(voyage, 'rpm');
    } catch (error) {
      console.error('fetchRpmData error:', error);
      return null;
    }
  };

  /**
   * Fetch flowmeter (fuel consumption) data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} Flowmeter data or null
   */
  const fetchFlowmeterData = async (voyage) => {
    try {
      return await fetchTelemetryByType(voyage, 'flowmeter');
    } catch (error) {
      console.error('fetchFlowmeterData error:', error);
      return null;
    }
  };

  /**
   * Load all voyage data (convenience method)
   * @param {Object} context - Context object with voyageId
   * @returns {Promise<Object>} Object with all loaded data
   */
  const loadAllVoyageData = async ({ voyageId, latestData = false, onlyFirstRequestLatestData = false }) => {
    const summary = await fetchSummary(voyageId, {
      latestData,
      onlyFirstRequestLatestData,
    });
    const voyage = summary?.master || null;

    return {
      voyage, summary,
    };
  };

  /**
   * Load telemetry data (GPS, RPM, Flowmeter) for a voyage
   * @param {Object} voyage - Voyage object
   * @returns {Promise<Object>} Object with gpsData, rpmData, flowmeterData
   */
  const loadTelemetryData = async (voyage) => {
    const [gpsData, rpmData, flowmeterData] = await Promise.all([
      fetchGpsData(voyage),
      fetchRpmData(voyage),
      fetchFlowmeterData(voyage),
    ]);

    return { gpsData, rpmData, flowmeterData };
  };

  return {
    // Individual fetch methods
    fetchVoyageSummaryData,
    fetchSummary,
    fetchGpsData,
    fetchRpmData,
    fetchFlowmeterData,
    getTelemetryAggregationParams,
    buildTelemetryChunks,
    mergeTelemetryResponses,
    // Convenience methods
    loadAllVoyageData,
    loadTelemetryData,
  };
}

export default useVoyageData;
